import { z } from 'zod';
import {
  AgentSessionView,
  EnsureWorkItem,
  EnsureWorkResult,
  EvidenceView,
  FinishAgentSession,
  ProgressEntryView,
  RecordProgress,
  ProjectRef,
  SetBlocker,
  SetWorkProjectOutcome,
  StartAgentSession,
  TransitionWork,
  WorkCandidate,
  WorkItemListItem,
  WorkItemView,
  AttachEvidence,
} from './work.js';
import { ConfirmRequest, PrepareRequest, PrepareRequestResult, RequestListItem, RequestView, SubmitRequest, TransitionRequest } from './requests.js';
import { AddComment, CommentView } from './collaboration.js';
import { LinkCodeFields, LinkCodeRefinement, LinkCodeResult } from './delivery.js';
import { QueryReport, ReportResult } from './reports.js';
import { CompletionPolicy, Priority, WorkPhase } from './enums.js';
import { ContentLocale, IsoDateTime, Title, Uuid, Version } from './common.js';
import { routes } from './routes.js';
import type { RouteDefinition } from './route.js';

/** Client-generated idempotency key carried by every mutating tool. */
const RequestId = z.string().min(8).max(100).describe('Client-generated idempotency key, stable across retries of the same call');

export const McpScope = z.enum(['work:read', 'work:write', 'requests:read', 'requests:write', 'reports:read', 'docs:read', 'docs:write']);
export type McpScope = z.infer<typeof McpScope>;

export interface McpToolDefinition {
  name: string;
  title: string;
  description: string;
  input: z.ZodObject;
  output: z.ZodType;
  /** The connection needs at least one of these scopes. */
  scope: McpScope | readonly McpScope[];
  annotations: { readOnlyHint: boolean; destructiveHint: boolean; idempotentHint: boolean; openWorldHint: boolean };
}

const read = { readOnlyHint: true, destructiveHint: false, idempotentHint: true, openWorldHint: false };
const write = { readOnlyHint: false, destructiveHint: false, idempotentHint: true, openWorldHint: false };

// ---------------------------------------------------------------- Operation gateway (UI parity)

/**
 * Every REST operation the web app uses is reachable from MCP through four generic tools, so agents
 * can do what the person can do in the UI. Reads are GET operations; everything else is a write.
 * Scopes follow the operation's area; roles and record access are enforced by the shared services.
 */
export type McpOperationAccess = 'read' | 'write';

const REQUEST_TAGS = new Set(['requests', 'intake']);
const SHARED_TAGS = new Set(['me', 'notifications', 'views', 'jobs', 'comments', 'evidence', 'attachments', 'translations', 'runtime']);
/**
 * Company documents have their own scopes (ADR-0040 §8, ADR-0048). Only signed-in document routes are
 * reachable: anonymous ones, the public links with passwords (ADR-0040 §5, ADR-0056), stay out.
 */
const DOCUMENT_TAGS = new Set(['documents']);
/**
 * Areas no connection reaches. Third-party integrations (ADR-0054) need the person's consent in a
 * browser and act with the person's own credential for another service.
 */
const UNEXPOSED_TAGS = new Set<string>(['integrations']);
/**
 * Browser-only operations: co-editing sync and presence heartbeats carry no agent attribution and
 * would store edits outside agent revisions (ADR-0051); agents use the document tools instead.
 * Public link management (ADR-0056) is for people in the app: agents never read a public link token
 * or set its password.
 */
const UNEXPOSED_OPERATIONS = new Set<string>([
  'syncDocumentCollab',
  'reportDocumentPresence',
  'getDocumentPublicLink',
  'rotateDocumentPublicLink',
  'setDocumentPublicPassword',
  'clearDocumentPublicPassword',
]);

export function mcpOperationAccess(route: RouteDefinition): { access: McpOperationAccess; scopes: McpScope[] } {
  const access: McpOperationAccess = route.method === 'GET' ? 'read' : 'write';
  if (UNEXPOSED_TAGS.has(route.tag) || UNEXPOSED_OPERATIONS.has(route.operationId)) return { access, scopes: [] };
  if (DOCUMENT_TAGS.has(route.tag)) return { access, scopes: route.auth === 'session' ? [access === 'read' ? 'docs:read' : 'docs:write'] : [] };
  if (REQUEST_TAGS.has(route.tag)) return { access, scopes: [access === 'read' ? 'requests:read' : 'requests:write'] };
  // Report queries and exports read data even when they are POST requests.
  if (route.tag === 'reports') return { access, scopes: ['reports:read'] };
  if (SHARED_TAGS.has(route.tag)) return { access, scopes: access === 'read' ? ['work:read', 'requests:read', 'docs:read'] : ['work:write', 'requests:write', 'docs:write'] };
  return { access, scopes: [access === 'read' ? 'work:read' : 'work:write'] };
}

/** Registry operations reachable through the gateway. */
export function mcpOperations(): Array<[string, RouteDefinition]> {
  return Object.entries(routes as Record<string, RouteDefinition>).sort(([a], [b]) => a.localeCompare(b));
}

const OperationId = z.string().min(1).max(80).describe('operationId from list_operations, for example createProject');
const OperationArgs = {
  operationId: OperationId,
  params: z.record(z.string(), z.string()).optional().describe('Path parameters, for example { "id": "<uuid>" }'),
  query: z.record(z.string(), z.string()).optional().describe('Query string parameters as strings'),
};
const OperationSummary = z.object({
  operationId: z.string(),
  method: z.string(),
  path: z.string(),
  summary: z.string(),
  tag: z.string(),
  access: z.enum(['read', 'write']),
  idempotent: z.boolean(),
});
const OperationResult = z.object({ data: z.unknown(), nextCursor: z.string().nullable().optional() });
const readAny: McpScope[] = ['work:read', 'requests:read', 'reports:read', 'docs:read'];
const writeAny: McpScope[] = ['work:write', 'requests:write', 'reports:read', 'docs:write'];

/** Most work items one set_work_project call changes; each item keeps its own version and outcome. */
export const SET_WORK_PROJECT_MAX_ITEMS = 25;
const SetWorkProjectItem = z
  .object({
    workItemId: z.string().min(1).max(60).describe('Work item UUID or key such as VAM-42'),
    expectedVersion: Version.describe('The version you read; a changed item fails with VERSION_CONFLICT'),
  })
  .strict();
const SetWorkProjectItemResult = z.object({
  workItemId: z.string(),
  key: z.string().nullable(),
  outcome: z.union([SetWorkProjectOutcome, z.literal('failed')]),
  previousProjectId: Uuid.nullable().optional(),
  workItem: z.object({ id: Uuid, key: z.string(), version: Version, phase: WorkPhase, completionPolicy: CompletionPolicy, project: ProjectRef.nullable() }).optional(),
  error: z.object({ code: z.string(), message: z.string(), details: z.unknown().optional() }).optional(),
});

// ---------------------------------------------------------------- Documents (ADR-0040 §8, ADR-0048)

/**
 * Longest Markdown a document tool accepts, in characters. MCP request bodies are capped at 256 KB and a
 * character can take up to 3 bytes in UTF-8 plus JSON escaping, so this stays well below a third.
 */
export const DOCUMENT_MARKDOWN_MAX = 60_000;
const AgentMarkdown = z
  .string()
  .max(DOCUMENT_MARKDOWN_MAX)
  .describe(
    'GitHub-flavored Markdown. Links to /docs/<documentId> become page chips and links to /go/<KEY> (VAM-12, REQ-4) become task chips, as read_document shows them. Person @mentions cannot be written here and stay plain text.',
  );
const DocumentUrlField = z.string().describe('Path of the document in the web app, relative to its origin');
const FolderPathField = z.string().describe('Folder names from the top level, separated by " / "');
const DocumentLinksView = z.object({
  pages: z.array(z.object({ id: Uuid, label: z.string(), title: z.string().nullable().describe('Current title, or null when you cannot read that document') })),
  work: z.array(z.object({ key: z.string(), label: z.string() })),
  people: z.array(z.object({ id: Uuid, label: z.string() })),
});
const DocumentWriteResult = z.object({ id: Uuid, title: z.string(), version: Version, url: DocumentUrlField, folderPath: FolderPathField });
const DocumentEditMode = z.enum(['replace', 'append', 'section']);

export const ContextView = z.object({
  user: z.object({ id: Uuid, displayName: z.string(), locale: z.string(), timezone: z.string() }),
  capabilities: z.array(z.string()),
  scopes: z.array(McpScope),
  /** documentationFolder: the project's documentation folder (ADR-0050), with docs:read and when readable. */
  projects: z.array(z.object({ id: Uuid, key: z.string(), name: z.string(), documentationFolder: z.object({ id: Uuid, name: z.string() }).optional() })),
  teams: z.array(z.object({ id: Uuid, name: z.string(), role: z.string() })),
  activeSessions: z.array(z.object({ id: Uuid, client: z.string(), clientSessionRef: z.string(), workItemKeys: z.array(z.string()) })),
  guidance: z.string(),
});

export const mcpTools = {
  get_context: {
    name: 'get_context',
    title: 'Get work-tracking context',
    description:
      'Returns the authenticated person, capabilities, granted scopes, accessible projects/teams and active agent sessions. Call first in a session. Never returns an organization-wide user list.',
    input: z.object({ clientSessionRef: z.string().max(200).optional() }),
    output: ContextView,
    // A documents-only connection starts here too (ADR-0048).
    scope: ['work:read', 'docs:read'],
    annotations: read,
  },
  search_work: {
    name: 'search_work',
    title: 'Search work items',
    description:
      'Permission-filtered candidate search with explainable association signals. Results are suggestions only: similarity never auto-links a session to a task. Max 20 hits.',
    input: z.object({
      query: z.string().min(1).max(500),
      projectId: Uuid.optional(),
      repositoryFullName: z.string().max(200).optional(),
      includeDone: z.boolean().optional(),
    }),
    output: z.object({ hits: z.array(WorkCandidate.extend({ phase: z.string(), updatedAt: z.string() })) }),
    scope: 'work:read',
    annotations: read,
  },
  get_work_item: {
    name: 'get_work_item',
    title: 'Get a work item',
    description: 'Current work item state, permitted recent progress (max 20 entries) and evidence. Accepts a UUID or display key such as VAM-42.',
    input: z.object({ idOrKey: z.string().min(1).max(60) }),
    output: z.object({ workItem: WorkItemView, recentProgress: z.array(ProgressEntryView), evidence: z.array(EvidenceView) }),
    scope: 'work:read',
    annotations: read,
  },
  search_requests: {
    name: 'search_requests',
    title: 'Search requests',
    description: 'Authorized requests and their functional state. Max 20 hits.',
    input: z.object({ query: z.string().max(500).optional(), status: z.string().max(100).optional(), mine: z.boolean().optional() }),
    output: z.object({ hits: z.array(RequestListItem) }),
    scope: 'requests:read',
    annotations: read,
  },
  get_request: {
    name: 'get_request',
    title: 'Get a request',
    description: 'Request detail with functional progress, recent comments and requester feedback. Accepts a UUID or key such as REQ-12.',
    input: z.object({ idOrKey: z.string().min(1).max(60) }),
    output: z.object({ request: RequestView, recentComments: z.array(CommentView) }),
    scope: 'requests:read',
    annotations: read,
  },
  ensure_work_item: {
    name: 'ensure_work_item',
    title: 'Ensure a work item',
    description:
      'Resolve an explicit key or stored session/branch/PR binding, or create one coherent unit of work idempotently by workIntentId. Ambiguity returns needs_selection with candidates and makes no hidden choice. Persist workIntentId locally before the first call.',
    input: EnsureWorkItem.extend({ requestId: RequestId }),
    output: EnsureWorkResult,
    scope: 'work:write',
    annotations: write,
  },
  start_work_session: {
    name: 'start_work_session',
    title: 'Start or resume a work session',
    description: 'Start or resume an agent session for the authenticated person. Unique per person, client and clientSessionRef.',
    input: StartAgentSession.extend({ requestId: RequestId }),
    output: AgentSessionView,
    scope: 'work:write',
    annotations: write,
  },
  record_progress: {
    name: 'record_progress',
    title: 'Record progress',
    description:
      'Append a bounded milestone summary (what changed, why, how, checks actually executed with claimBasis, results, pending work). Append-only; never overwrites human edits. Agent-reported checks stay distinct from CI-observed results.',
    input: RecordProgress.extend({ sessionId: Uuid, requestId: RequestId }),
    output: ProgressEntryView,
    scope: 'work:write',
    annotations: write,
  },
  set_work_phase: {
    name: 'set_work_phase',
    title: 'Request a work phase transition',
    description:
      'Validated transition under the completion policy. Cannot assert unproven production delivery: PRODUCTION work reaches DONE only from verified deployment evidence. Closing a BUG also needs its cause: send defectCause with the work that caused it (CAUSED_BY) or NO_DELIVERY_CAUSE with a reason (PRE_EXISTING, EXTERNAL, CONFIGURATION, DATA, or OTHER with a note of up to 500 characters), or ask the person; without it the close fails with DEFECT_CAUSE_REQUIRED.',
    input: TransitionWork.extend({ workItemId: Uuid, requestId: RequestId }),
    output: WorkItemView,
    scope: 'work:write',
    annotations: write,
  },
  set_blocker: {
    name: 'set_blocker',
    title: 'Open or resolve a blocker',
    description: 'Open a blocker with a reason (optionally preventing completion) or resolve one.',
    input: z.object({ workItemId: Uuid, requestId: RequestId, blocker: SetBlocker }),
    output: WorkItemView,
    scope: 'work:write',
    annotations: write,
  },
  set_work_project: {
    name: 'set_work_project',
    title: 'Assign a project to work items',
    description:
      'Move up to 25 existing work items into one project, each with the version you read. Uses the same edit permission, project access check, audit trail and visibility rules as the web app; phase, completion policy and acceptance criteria do not change. projectId is required and never null: the server picks no default project. With onlyIfMissing, items that already have a project are kept (outcome kept), so a fallback project never overrides a specific one. Each item reports its own outcome (updated, unchanged, kept or failed with an error code); one failure does not stop the others. An unexpected server error stops the call: earlier results are still returned, the item fails with INTERNAL_ERROR and the rest with NOT_ATTEMPTED. Retrying with the same requestId replays stored results and runs the rest.',
    input: z
      .object({
        requestId: RequestId,
        projectId: Uuid.describe('Target project id from get_context. Required; null is rejected.'),
        onlyIfMissing: z.boolean().optional().describe('true: only assign items without a project; keep any existing project'),
        items: z
          .array(SetWorkProjectItem)
          .min(1)
          .max(SET_WORK_PROJECT_MAX_ITEMS)
          .refine((items) => new Set(items.map((i) => i.workItemId.trim().toUpperCase())).size === items.length, { message: 'each work item appears once' }),
      })
      .strict(),
    output: z.object({
      projectId: Uuid,
      results: z.array(SetWorkProjectItemResult),
      counts: z.object({ updated: z.number().int(), unchanged: z.number().int(), kept: z.number().int(), failed: z.number().int() }),
    }),
    scope: 'work:write',
    annotations: write,
  },
  attach_evidence: {
    name: 'attach_evidence',
    title: 'Attach evidence',
    description: 'Add a source reference. The server assigns AGENT_REPORTED basis; only trusted adapters can produce PROVIDER_VERIFIED evidence.',
    input: AttachEvidence.extend({ requestId: RequestId }),
    output: EvidenceView,
    scope: 'work:write',
    annotations: write,
  },
  link_code: {
    name: 'link_code',
    title: 'Link code to a work item',
    description:
      'Link the code behind a work item so its Code tab opens it on GitHub. Pass the repository (owner/repo from the git remote) with the branch you work on (links its pull requests and the commits ahead of the default branch), pushed commit SHAs, PR numbers and/or GitHub URLs. Call it after creating or updating the work item and after each push or PR. Unpushed commits and unknown repositories are reported in skipped, never as errors. Idempotent.',
    input: LinkCodeFields.extend({ workItemId: z.string().min(1).max(60).describe('Work item UUID or key such as VAM-42'), requestId: RequestId }).refine(LinkCodeRefinement.check, LinkCodeRefinement.message),
    output: LinkCodeResult,
    scope: 'work:write',
    annotations: write,
  },
  finish_work_session: {
    name: 'finish_work_session',
    title: 'Finish a work session',
    description: 'End the session and record a handoff. Ending a session never marks work done.',
    input: FinishAgentSession.extend({ sessionId: Uuid, requestId: RequestId }),
    output: AgentSessionView,
    scope: 'work:write',
    annotations: write,
  },
  prepare_request: {
    name: 'prepare_request',
    title: 'Prepare a request draft',
    description:
      'Create or update a structured request draft as the authenticated requester. Returns missing fields and authorized duplicate candidates. Unknown impact is valid. Does not submit.',
    input: PrepareRequest.extend({ requestId: RequestId }),
    output: PrepareRequestResult,
    scope: 'requests:write',
    annotations: write,
  },
  submit_request: {
    name: 'submit_request',
    title: 'Submit a reviewed request',
    description: 'Submit the draft version the person reviewed. Returns the canonical request URL.',
    input: SubmitRequest.extend({ requestId: RequestId }),
    output: RequestView,
    scope: 'requests:write',
    annotations: write,
  },
  confirm_request: {
    name: 'confirm_request',
    title: 'Confirm or report a resolved request',
    description: 'Record the authenticated requester’s CONFIRMED or PERSISTS response for the displayed fulfillment generation.',
    input: ConfirmRequest.extend({ id: Uuid, requestId: RequestId }),
    output: RequestView,
    scope: 'requests:write',
    annotations: write,
  },
  triage_request: {
    name: 'triage_request',
    title: 'Triage a request',
    description: 'Apply an allowed transition, assignment or priority change, each checked against the person’s capabilities.',
    input: z.object({
      id: Uuid,
      requestId: RequestId,
      expectedVersion: Version,
      transition: TransitionRequest.omit({ expectedVersion: true }).optional(),
      assigneeId: Uuid.nullable().optional(),
      priority: Priority.optional(),
    }),
    output: RequestView,
    scope: 'requests:write',
    annotations: write,
  },
  add_comment: {
    name: 'add_comment',
    title: 'Comment',
    description: 'Comment on an accessible request or work item.',
    input: AddComment.extend({ requestId: RequestId }),
    output: CommentView,
    scope: 'work:write',
    annotations: write,
  },
  query_report: {
    name: 'query_report',
    title: 'Query a report',
    description: 'Execute an allowlisted report. Tier and row-level permissions are identical to the web UI and CSV export.',
    input: QueryReport,
    output: ReportResult,
    scope: 'reports:read',
    annotations: read,
  },
  search_documents: {
    name: 'search_documents',
    title: 'Search documents',
    description:
      "Company documents you can read that match the query, most recently updated first (max 20). Documents in folders you cannot see and other people's locked drafts never appear. Open one with read_document.",
    input: z.object({ q: z.string().min(1).max(200).describe('Words to look for'), limit: z.number().int().min(1).max(20).optional() }).strict(),
    output: z.object({ hits: z.array(z.object({ id: Uuid, title: z.string(), folderId: Uuid, folderPath: FolderPathField, url: DocumentUrlField, updatedAt: IsoDateTime })) }),
    scope: 'docs:read',
    annotations: read,
  },
  read_document: {
    name: 'read_document',
    title: 'Read a document',
    description:
      'A document you can read, as Markdown without its title, with its version, folder path, web URL and outgoing links: page chips, task chips and @mentioned people. Pass heading to read one section of a long document. Text longer than the result limit is cut with a marker and truncated=true: never send truncated text back with update_document mode replace; edit by section or append instead. A document you cannot read is NOT_FOUND.',
    input: z
      .object({
        id: Uuid,
        heading: z.string().min(1).max(300).optional().describe('Return only this section: from the heading to the next heading of the same or a higher level. "## Goals" picks a level.'),
      })
      .strict(),
    output: z.object({
      id: Uuid,
      title: z.string(),
      version: Version.describe('Pass it as expectedVersion to update_document or link_document'),
      url: DocumentUrlField,
      folderId: Uuid,
      folderPath: FolderPathField,
      parentDocumentId: Uuid.nullable(),
      locked: z.boolean().describe('A private draft visible only to its creator and the people they named'),
      canEdit: z.boolean(),
      updatedAt: IsoDateTime,
      section: z.string().nullable(),
      markdown: z.string(),
      truncated: z.boolean(),
      totalCharacters: z.number().int(),
      links: DocumentLinksView,
    }),
    scope: 'docs:read',
    annotations: read,
  },
  create_document: {
    name: 'create_document',
    title: 'Create a document',
    description:
      'Create a document from Markdown in a folder where the person edits (folder ids come from search_documents or the listDocumentFolders operation), optionally as a subpage of a document in that folder or as a locked private draft. Same permissions as the web app; the history records it as written by this agent. Show the person the title and folder first.',
    input: z
      .object({
        requestId: RequestId,
        folderId: Uuid,
        parentDocumentId: Uuid.optional().describe('Create it as a subpage of this document (same folder)'),
        title: Title,
        markdown: AgentMarkdown.optional(),
        locked: z.boolean().optional().describe('Start as a private draft only the person sees'),
        originalLocale: ContentLocale.optional(),
      })
      .strict(),
    output: DocumentWriteResult,
    scope: 'docs:write',
    annotations: write,
  },
  update_document: {
    name: 'update_document',
    title: 'Edit a document',
    description:
      "Edit a document with Markdown, as the person and with the web app's edit permissions. mode replace rewrites the whole content; append adds to the end; section replaces the content under heading up to the next heading of the same or a higher level, keeping the heading unless the Markdown starts with a heading of that level (which renames it). A missing heading is an error that lists the document's headings. expectedVersion is the version you read: if the document changed since, the call fails with VERSION_CONFLICT and changes nothing; read it again and reapply. The history records the edit as made by this agent. Confirm substantial rewrites with the person first.",
    input: z
      .object({
        id: Uuid,
        expectedVersion: Version.describe('The version from read_document'),
        mode: DocumentEditMode,
        markdown: AgentMarkdown.min(1),
        heading: z.string().min(1).max(300).optional().describe('Required for mode section'),
        title: Title.optional().describe('Also rename the document'),
      })
      .strict()
      .refine((v) => v.mode !== 'section' || !!v.heading, { message: 'heading is required for mode section', path: ['heading'] }),
    output: DocumentWriteResult.extend({ mode: DocumentEditMode, changed: z.boolean() }),
    scope: 'docs:write',
    annotations: { readOnlyHint: false, destructiveHint: true, idempotentHint: false, openWorldHint: false },
  },
  link_document: {
    name: 'link_document',
    title: 'Link a document to work or another document',
    description:
      'Add a "Related:" line with a task chip (workKey such as VAM-123 or REQ-4) or a page chip (documentId) at the end of a document, so the link shows in the document and on the target. The person must be able to read the target and edit the document. A target the document already links to is reported as already_linked, without a change.',
    input: z
      .object({
        id: Uuid.describe('The document to add the link to'),
        expectedVersion: Version.describe('The version from read_document'),
        workKey: z.string().min(3).max(30).optional().describe('Work item or request key, for example VAM-123'),
        documentId: Uuid.optional().describe('Another document to link to'),
      })
      .strict()
      .refine((v) => (v.workKey ? 1 : 0) + (v.documentId ? 1 : 0) === 1, { message: 'pass exactly one of workKey or documentId' }),
    output: DocumentWriteResult.extend({
      outcome: z.enum(['linked', 'already_linked']),
      target: z.object({ kind: z.enum(['WORK_ITEM', 'REQUEST', 'DOCUMENT']), id: Uuid, key: z.string().nullable(), title: z.string() }),
    }),
    scope: 'docs:write',
    annotations: write,
  },
  list_operations: {
    name: 'list_operations',
    title: 'List available operations',
    description:
      'Lists the web app operations this connection can use (everything a person can do in the UI: projects, teams, work, requests, documents and folders, comments, notifications, views, reports, administration). Use it when no dedicated tool fits, then describe_operation and read_operation or write_operation.',
    input: z.object({ tag: z.string().max(40).optional().describe('Filter by area, for example projects'), search: z.string().max(100).optional().describe('Filter by words in the operationId or summary') }),
    output: z.object({ operations: z.array(OperationSummary) }),
    scope: readAny,
    annotations: read,
  },
  describe_operation: {
    name: 'describe_operation',
    title: 'Describe an operation',
    description: 'Returns the JSON Schemas of an operation\'s path parameters, query and body, so you can call it correctly.',
    input: z.object({ operationId: OperationId }),
    output: OperationSummary.extend({ params: z.unknown().nullable(), query: z.unknown().nullable(), body: z.unknown().nullable() }),
    scope: readAny,
    annotations: read,
  },
  read_operation: {
    name: 'read_operation',
    title: 'Run a read operation',
    description: 'Runs a read-only (GET) operation as the signed-in person, with the same permissions as the web app. Collections return data plus nextCursor; pass it back as query.cursor for the next page.',
    input: z.object(OperationArgs),
    output: OperationResult,
    scope: readAny,
    annotations: read,
  },
  write_operation: {
    name: 'write_operation',
    title: 'Run a write operation',
    description:
      'Runs a create, update or other changing operation as the signed-in person, with the same validation, permissions and conflict checks as the web app. Show the person what will change and get confirmation first. requestId makes retries safe for operations that support idempotency.',
    input: z.object({ ...OperationArgs, body: z.unknown().optional().describe('JSON body as described by describe_operation'), requestId: RequestId }),
    output: OperationResult,
    scope: writeAny,
    annotations: { readOnlyHint: false, destructiveHint: true, idempotentHint: false, openWorldHint: false },
  },
} satisfies Record<string, McpToolDefinition>;

/** Whether a connection with these scopes may use a tool. */
export function mcpToolAllowed(tool: Pick<McpToolDefinition, 'scope'>, scopes: ReadonlySet<string>): boolean {
  const needed = typeof tool.scope === 'string' ? [tool.scope] : tool.scope;
  return needed.some((s) => scopes.has(s));
}

export type McpToolName = keyof typeof mcpTools;
/** The structured result a tool returns. */
export type McpToolOutput<K extends McpToolName> = z.output<(typeof mcpTools)[K]['output']>;
export { WorkItemListItem };
